import React, { useState, useEffect, useContext, useMemo } from "react"
import dayjs from "dayjs"
import { useShallow } from "zustand/react/shallow"
import DiffMatchPatch from "diff-match-patch"
import parse from "html-react-parser"

// components
import Label from "components/LegacyLabel/Label"
import { Tooltip } from "antd"

// apis
import { useGetAuthoringStudy } from "api/hooks/studies/useAuthoringApi"
import { useUserSettings } from "api/hooks"

// context
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useAuthoringVersionStore from "../../../hooks/stores/useAuthoringVersionStore"
import useAuthoringStore from "../../../hooks/stores/useAuthoringStore"
import useAuthoringTranslations from "../../../hooks/stores/useAuthoringTranslations"

// utils
import { highlightHtmlString } from "utilities/misc"
import "./RJReadOnly.scss"

const RJReadOnly = (props) => {
  const {
    id,
    formSection,
    carbonProps,
    overrideJpath,
    isTranslation,
    jpathDotNotation,
    schema,
    isForked,
    forkedRegistryKey,
  } = props

  const { authoringId, lockedFullname, isUserLocked } = useAuthoringStore(
    useShallow((state) => ({
      authoringId: state.authoringId,
      lockedFullname: state.lockedFullname,
      isUserLocked: state.isUserLocked,
    }))
  )

  const { selectedVersion, versionData, versionValues } = useAuthoringVersionStore(
    useShallow((state) => ({
      selectedVersion: state.selectedVersion,
      versionData: state.versionData,
      versionValues: state.versionValues,
    }))
  )

  const { isReviewerView, formView, showVersionCompare } = useAuthoringViewStore(
    useShallow((state) => ({
      isReviewerView: state.isReviewerView,
      formView: state.formView,
      showVersionCompare: state.showVersionCompare,
    }))
  )

  const { selectedLanguage } = useAuthoringTranslations(
    useShallow((state) => ({
      selectedLanguage: state.selectedLanguage,
    }))
  )

  const { data: authoring } = useGetAuthoringStudy(authoringId)
  const { data: userSettings } = useUserSettings()

  const [value, setValue] = useState("")
  const [verDiff, setVerDiff] = useState([])
  const [htmlText, setHtmlText] = useState("")
  const jpath = id

  useEffect(() => {
    let value = formatValue(props.value)
    setValue(value)
  }, [props.value, value, formView?.id])

  const getOptionValue = (value) => {
    return schema.options.find((item) => {
      if (typeof item === "string") {
        return item === "value"
      } else if (typeof item === "object" && item !== null) {
        return item.value === value
      }
      return null
    })
  }

  const isArrayOfStrings = (value) => {
    // Check if the value is an array
    if (Array.isArray(value)) {
      // Check if all elements in the array are strings
      return value.every((item) => typeof item === "string")
    }
    // If not an array, return false
    return false
  }

  const formatValue = (value) => {
    if (schema?.options && !isTranslation) {
      if (Array.isArray(value)) {
        if (isArrayOfStrings(value)) return value.join(", ")

        let ret = value.map((val) => getOptionValue(val)).filter((item) => item)

        return ret.map((item) => item.label).join(", ")
      }

      let foundOption = schema.options.find((item) => item.value === value)
      if (foundOption?.registries?.[formView.id]) return foundOption.registries[formView.id]

      if (foundOption?.label && schema?.valueMapping?.[formView?.id]?.[foundOption.value]) {
        if (isTranslation) {
          foundOption = {
            value: foundOption.value,
            label: props.value,
          }
        } else {
          foundOption = {
            value: foundOption.value,
            label: schema?.valueMapping?.[formView?.id]?.[foundOption.value],
          }
        }
      }
      return foundOption?.label || value
    } else if (typeof value === "boolean") {
      if (value === true) return "Yes"
      else return "No"
    } else if (value && Array.isArray(value)) {
      return value.filter((item) => !item !== null && !item !== undefined)?.join(", ")
    } else if (typeof value === "object" && value !== null) {
      return null
    }

    return value
  }

  // On Study Data, previous version data to compare to current value.
  // On Reviewer view, if form-version URL param, version value to display. Live value if no version in URL params.
  const versionValue = useMemo(() => {
    if (isReviewerView && !selectedVersion) {
      let ret = formatValue(props.value)
      return ret
    }

    const selectedVersNum = selectedVersion?.version_num
    // if selected version is not current study version

    if (versionValues && selectedVersNum) {
      const selectedVersValues = versionValues?.[selectedVersNum]
      const studyData = selectedVersValues?.study_data
      const forkedData = selectedVersValues?.forked_data

      let regKey = formView?.id
      if (regKey === "eudra") regKey = "eudract"

      let originalPath = null

      if (isTranslation && selectedLanguage) {
        return versionData?.study_data.translations?.[selectedLanguage]?.[jpathDotNotation]
      } else if (isForked) {
        return forkedData?.[forkedRegistryKey]?.[jpathDotNotation]
      } else if (studyData?.[jpathDotNotation]) {
        return studyData?.[jpathDotNotation]?.value
      } else if (formView?.id === "global" && id) {
        let ids = id?.split("_")
        let regKey = ids[ids?.length - 1]
        originalPath = jpath.replace(`_${regKey}`, "")
        if (studyData?.[originalPath]) {
          return studyData?.[originalPath]?.value
        }
      }

      return null
    }

    return null
  }, [selectedVersion, versionValues, formSection, formView, versionData, selectedLanguage])

  useEffect(() => {
    if (versionData && (value || versionValue || versionData?.override_data?.[overrideJpath])) {
      formatVersionComparison()
    }
  }, [value, selectedVersion, versionValue, versionData])

  const formatVersionComparison = () => {
    let overrideValue = versionData?.override_data?.[overrideJpath]
    let compareValue = versionValue

    if (overrideValue) compareValue = overrideValue
    if (isTranslation) compareValue = versionValue

    let formattedCompareValue = formatValue(compareValue) || ""
    let formattedValue = value || ""

    if (selectedVersion && (formattedCompareValue || value)) {
      const dmp = new DiffMatchPatch()
      if (isArrayOfStrings(formattedCompareValue)) formattedCompareValue = formattedCompareValue.join(", ")
      if (isArrayOfStrings(formattedValue)) formattedValue = formattedValue.join(", ")
      if (typeof formattedCompareValue !== "string") formattedCompareValue = formattedCompareValue.toString()
      if (typeof formattedValue !== "string") formattedValue = formattedValue.toString()
      let diff = dmp.diff_main(formattedCompareValue, formattedValue)
      dmp.diff_cleanupSemantic(diff)

      setVerDiff(diff)
    }
  }

  // set html text if verDiff
  useEffect(() => {
    if (verDiff) {
      let html = ""

      verDiff.forEach((text) => {
        let type = text[0]
        let value = text[1]
        if (type === 0) {
          html += highlightHtmlString(value, "highlight-identical")
        } else if (type === 1) {
          html += highlightHtmlString(value, "highlight-new")
        } else if (type === -1) {
          html += highlightHtmlString(value, "highlight-old")
        }
      })
      setHtmlText(html)
    }
  }, [verDiff])

  const content = () => {
    let displayValue = value

    if (isReviewerView) displayValue = formatValue(props.value)

    if (props?.uiSchema?.["ui:widget"] === "date" && displayValue) {
      displayValue = dayjs(displayValue).format(userSettings?.date_format)
    }

    let _content = (
      <div
        id={`${id}-field`}
        className={`rj-read-only`}
      >
        {typeof displayValue === "string" || typeof displayValue === "number" ? displayValue : <br />}
      </div>
    )

    if (selectedVersion && showVersionCompare) {
      return (
        <div
          id={`${id}-field`}
          className={`rj-read-only`}
        >
          {htmlText ? parse(htmlText) : <span>&nbsp;</span>}
        </div>
      )
    }

    let message = (
      <>
        This form is locked <span className="text-nowrap">by {authoring?.locked_by_fullname}</span>.
      </>
    )
    if (lockedFullname === "ETL Load") message = "This form is locked by ETL Load"

    // user locking
    if (isUserLocked && !isReviewerView) _content = <Tooltip title={message}>{_content}</Tooltip>

    return _content
  }

  return (
    <div
      className="rj-read-only-container"
      style={carbonProps?.styles && carbonProps.styles.widget}
    >
      {props.labelText && <Label>{props.labelText}</Label>}
      {content()}
    </div>
  )
}

export default RJReadOnly
